import NiceModal from '@ebay/nice-modal-react';
import { css } from '@emotion/react';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import AutoComplete from 'components/field/AutoComplete';
import AutoString from 'components/field/AutoString';
import Button from 'components/field/Button';
import CurrencyInput from 'components/field/CurrencyInput';
import DateInput from 'components/field/DateInput';
import EnumSelect from 'components/field/EnumSelect';
import IntegerInput from 'components/field/IntegerInput';
import StringInput from 'components/field/StringInput';
import TextArea from 'components/field/TextArea';
import FormItem from 'components/form/FormItem';
import Accordion from 'components/global/Accordion';
import Alert from 'components/global/Alert';
import Icon from 'components/global/Icon';
import ModuleToolTipContainer from 'components/global/ModuleToolTipContainer';
import NoRecordsFound from 'components/global/NoRecordsFound';
import ProductImage from 'components/global/ProductImage';
import Tooltip from 'components/global/Tooltip';
import Typography from 'components/global/Typography';
import Table from 'components/table/Table';

import useAccessControl from 'hooks/useAccessControl';
import useAlert from 'hooks/useAlert';
import useAppTracking from 'hooks/useAppTracking';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useDebounce from 'hooks/useDebounce';
import useLocale from 'hooks/useLocale';
import useMediaQuery from 'hooks/useMediaQuery';
import useRestriction, { SYSTEM_LIMITS } from 'hooks/useRestriction';
import useUpdateEffect from 'hooks/useUpdateEffect';

import BranchFormshort from 'pages/Branches/BranchFormShort';
import ProductForm from 'pages/Products/Products/ProductForm';
import BrandForm from 'pages/Settings/Brands/BrandForm';
import BoxRow from 'pages/Shipments/ActionForms/CreateShipment/Components/BoxRow';
import WarehouseFormShort from 'pages/Warehouses/WarehouseFormShort';

import { AUTOCOMPLETE, ORDERS, ORDER_ACTIONS, PRODUCTS } from 'services/consts';

import AppLogger from 'utils/AppLogger';
import { Breakpoints } from 'utils/const.styles';
import PATHS from 'utils/paths';
import { roundNumber } from 'utils/utils';

import { faCircleInfo } from '@fortawesome/pro-regular-svg-icons';
import { faEye, faPlus } from '@fortawesome/pro-solid-svg-icons';

import * as S from '../OrderForm.styled';

const largeDefaultColSpan = {
	md: 12
};

const defaultColSpan = {
	md: 6
};

const smallDefaultColSpan = {
	md: 3
};

const orderDetailsErrorFields = [
	'orderID',
	'orderDate',
	'paymentMethod',
	'grandTotal',
	'totalDue',
	'warehouseID',
	'branchID',
	'brandID',
	'refNo'
];
const productDetailsErrorFields = ['orderComments', 'products'];
const boxDetailsErrorFields = ['boxes', 'weight', 'packageCount'];

const OrderDetails = ({ otoOrderID, isAdmin, formMode, orderType }) => {
	const { Currency } = useLocale();
	const { showSuccess, showError } = useAlert();
	const axios = useAxiosPrivate();
	const isAuthorized = useAccessControl();
	const isRestricted = useRestriction();
	const { track } = useAppTracking();
	const isImportOrder = orderType === 'import';

	const { t } = useTranslation();
	const isMobile = useMediaQuery(Breakpoints.mobile);

	const { defaultBox: defaultBoxState, userType } = useSelector((state) => state.user);
	const defaultBox = useMemo(
		() =>
			defaultBoxState
				? {
						ID: defaultBoxState.id,
						width: defaultBoxState.width,
						height: defaultBoxState.height,
						length: defaultBoxState.length,
						name: defaultBoxState.boxName,
						weight: 0
					}
				: { width: 10, height: 10, length: 10, weight: 0 },
		[defaultBoxState]
	);

	const [generateButtonLoading, setGenerateButtonLoading] = useState(false);

	const formMethods = useFormContext();
	const {
		setValue,
		watch,
		control,
		getValues,
		clearErrors,
		setFocus,
		formState: { isLoading: isFormLoading, isSubmitting, errors, defaultValues }
	} = formMethods;

	const paymentMethods = useSelector((state) => state.user.generalSettings?.paymentMethods);

	const isLoading = isFormLoading || isSubmitting;

	// Watchers
	const manuallyAdded = useWatch({ control, name: 'manuallyAdded' }) || isAdmin;
	const currency = useWatch({ control, name: 'currency' });
	const grandTotal = useWatch({ control, name: 'grandTotal' });
	const paymentMethod = useWatch({ control, name: 'paymentMethod' });
	const brandID = useWatch({ control, name: 'brandID' });
	const warehouseID = useWatch({ control, name: 'warehouseID' });
	const branchID = useWatch({ control, name: 'branchID' });
	const productsWatcher = useWatch({ control, name: 'products' });
	const boxWatcher = useWatch({ control, name: 'boxes' });
	const isInternationalOrder = useWatch({ control, name: 'isInternationalOrder' });

	const orderDetailsAccordionRef = useRef(null);
	const productDetailsAccordionRef = useRef(null);
	const boxDetailsAccordionRef = useRef(null);

	const {
		fields,
		append: append,
		remove: remove
	} = useFieldArray({
		control,
		name: 'products'
	});

	const {
		fields: boxFields,
		append: appendBox,
		remove: removeBox
	} = useFieldArray({
		control,
		name: 'boxes'
	});

	const controlledFields = fields.map((field, index) => ({
		...field,
		...productsWatcher[index]
	}));

	const editable = controlledFields.filter((a) => a.itemType === 'bundle').length === 0;
	const hasWave = watch('waveID') !== null && watch('waveID') !== undefined;
	const { addMode, editMode, viewMode } = useMemo(
		() => ({
			addMode: formMode === 'add',
			editMode: formMode === 'edit',
			viewMode: formMode === 'view'
		}),
		[formMode]
	);
	const isCustomerServiceUser = userType === 'customerServiceUser';
	const grandTotalOfAllProducts = useMemo(
		() => controlledFields.reduce((acc, curr) => acc + (curr.total ?? 0), 0),
		[controlledFields]
	);

	const navigate = useNavigate();

	useEffect(() => {
		if (grandTotal && paymentMethod?.toLowerCase() === 'cod' && addMode) {
			setValue('totalDue', grandTotal, {
				shouldValidate: true,
				shouldDirty: true
			});
		}
	}, [grandTotal, paymentMethod, addMode]);

	useEffect(() => {
		// when payment method is changed to COD
		// and if the user has not previously save totalDue
		if (paymentMethod?.toLowerCase() === 'cod' && defaultValues.totalDue === 0) {
			if (defaultValues.paymentMethod === 'cod') {
				setValue('totalDue', defaultValues.totalDue, { shouldValidate: true, shouldDirty: true });
				return;
			}
			setValue('totalDue', grandTotal, { shouldValidate: true, shouldDirty: true });
		}
	}, [paymentMethod]);

	useEffect(() => {
		if (productsWatcher?.length && errors?.orderComments) {
			clearErrors('orderComments');
		}
	}, [productsWatcher, errors]);

	// Autocomplete configs
	const warehouseAutocompleteLabels = useMemo(() => ['Name'], []);
	const warehouseAutocompleteColumns = [
		{ accessor: 'Name', width: 200 },
		{ accessor: 'Code', width: 150 }
	];

	const branchAutocompleteLabels = useMemo(() => ['Name'], []);
	const branchAutocompleteColumns = [
		{ accessor: 'Name', width: 200 },
		{ accessor: 'Code', width: 150 }
	];
	const brandAutocompleteColumns = useMemo(() => [{ accessor: 'Name', width: 200 }], []);

	const productAutocompleteLabels = useMemo(() => ['name'], []);
	const productAutocompleteColumns = useMemo(
		() => [
			{ accessor: 'name', width: 200 },
			{ accessor: 'hsCode', width: 200 },
			{ accessor: 'SKU', width: 200 }
		],
		[]
	);

	const getAutoFillOrderID = useCallback(async () => {
		setGenerateButtonLoading(true);
		try {
			const response = await axios.post(ORDERS.GET_AUTO_FILL_ORDER_ID, {
				...(orderType ? { orderType } : {})
			});
			if (response.status === 200) {
				setValue('orderID', response?.data.orderID, { shouldValidate: true, shouldDirty: true });
				setGenerateButtonLoading(false);
			} else {
				AppLogger.error(response);
				showError();
			}
		} catch (error) {
			AppLogger.error(error);
			showError();
		}
	});

	const removeItem = async ({ index, original: { ID } }) => {
		if (ID) {
			try {
				const response = await axios.post(ORDER_ACTIONS.DELETE_ORDER_ITEM, {
					otoOrderID: Number(otoOrderID),
					ID
				});
				if (response.status === 200) {
					showSuccess();
					remove(index);
				} else {
					showError();
					AppLogger.error(response);
				}
			} catch (error) {
				AppLogger.error(error);
				showError();
			}
		} else {
			remove(index);
		}
	};

	const columns = useMemo(
		() => [
			{
				id: 'products',
				header: 'productName',
				size: isMobile ? 110 : 200,
				required: isImportOrder,
				renderCell: ({ row }) => (
					<div
						style={{
							display: 'flex',
							flexDirection: 'row'
						}}>
						<div
							style={{
								width: 'auto'
							}}>
							<ProductImage
								data={{
									image: watch(`products[${row.index}].image`)
								}}
							/>
						</div>
						<div
							style={{
								display: 'flex',
								flexDirection: 'column',
								width: '100%',
								overflow: 'auto'
							}}>
							{manuallyAdded || isInternationalOrder ? (
								<AutoComplete
									key={row.original?.id}
									autoCompleteName={AUTOCOMPLETE.PRODUCTS}
									value={watch(`products[${row.index}].productID`)}
									columns={productAutocompleteColumns}
									fixedContainerWidth={false}
									name={`products[${row.index}].name`}
									selectionLabelAccessor={productAutocompleteLabels}
									setValue={(value, isFirstRender) => {
										if (!isFirstRender) {
											setValue(`products[${row.index}].productID`, value?.ID ?? null, {
												shouldValidate: true,
												shouldDirty: true
											});
											setValue(`products[${row.index}].name`, value?.name ?? null, {
												shouldValidate: true,
												shouldDirty: true
											});
											setValue(`products[${row.index}].hsCode`, value?.hsCode ?? null, {
												shouldValidate: true,
												shouldDirty: true
											});
											setValue(`products[${row.index}].SKU`, value?.SKU ?? null, {
												shouldValidate: true,
												shouldDirty: true
											});
											setValue(`products[${row.index}].price`, value?.price ?? null, {
												shouldValidate: true,
												shouldDirty: true
											});
											setValue(`products[${row.index}].tax`, value?.tax ?? null, {
												shouldValidate: true,
												shouldDirty: true
											});
											setValue(
												`products[${row.index}].total`,
												((value?.price ?? 0) + (value?.tax ?? 0)) *
													getValues(`products[${row.index}].quantity`),
												{
													shouldValidate: true,
													shouldDirty: true
												}
											);
											setValue(`products[${row.index}].currency`, value?.currency ?? currency, {
												shouldValidate: true,
												shouldDirty: true
											});
											setValue(`products[${row.index}].image`, value?.image ?? null, {
												shouldValidate: true,
												shouldDirty: true
											});
										}
									}}
									firstItem={
										isAuthorized('/autocomplete/product/addANewProduct')
											? watch(`products[${row.index}].name`)
												? {
														action: () => setFocus(`products[${row.index}].SKU`),
														label: (
															<Typography color="var(--blue)">
																continueAsANewProduct
															</Typography>
														)
													}
												: {
														action: () =>
															NiceModal.show(ProductForm, {
																setProduct: (value) => {
																	setValue(
																		`products[${row.index}].productID`,
																		value,
																		{
																			shouldValidate: true,
																			shouldDirty: true
																		}
																	);
																},
																isImportOrder: false
															}),
														label: (
															<Typography color="var(--blue)">addNewProduct</Typography>
														)
													}
											: undefined
									}
									textValue={watch(`products[${row.index}].name`)}
									setTextValue={(value) => {
										setValue(`products[${row.index}].productID`, null, {
											shouldValidate: true,
											shouldDirty: true
										});

										setValue(`products[${row.index}].name`, value, {
											shouldValidate: true,
											shouldDirty: true
										});
									}}
									viewMode={viewMode || !editable || hasWave}
								/>
							) : (
								<StringInput viewMode name={`products[${row.index}].name`} />
							)}
						</div>
					</div>
				),
				showOnMobile: true,
				enableHiding: false
			},
			...(isInternationalOrder || watch(`products[${0}].hsCode`)
				? [
						{
							...(addMode
								? {
										header: () => (
											<S.CustomHeaderContainer>
												<Typography fontWeight="500">hsCode</Typography>
												{isInternationalOrder && <S.Required>*</S.Required>}
												<Tooltip title={'hsCodeTooltip'} placement="bottom">
													<Icon icon={faCircleInfo} size="small" />
												</Tooltip>
											</S.CustomHeaderContainer>
										)
									}
								: {
										header: 'hsCode',
										required: isImportOrder
									}),
							accessorKey: 'hsCode',
							size: isMobile ? 110 : 200,
							renderCell: ({ row }) => (
								<AutoStringCell
									hsCode={watch(`products[${row.index}].hsCode`)}
									setHsCode={(value) => {
										setValue(`products[${row.index}].hsCode`, value, {
											shouldValidate: true,
											shouldDirty: true
										});
									}}
									formMode={formMode}
									errorMessage={errors?.products?.[row.index]?.hsCode?.message}
								/>
							)
						}
					]
				: []),
			{
				header: 'sku',
				accessorKey: 'SKU',
				size: 90,
				required: isImportOrder,
				renderCell: ({ row }) => (
					<StringInput
						name={`products[${row.index}].SKU`}
						viewMode={watch(`products[${row.index}].productID`) || viewMode}
					/>
				)
			},
			...(formMode !== 'add' && !isImportOrder
				? [
						{
							header: 'barcode',
							accessorKey: 'barcode',
							size: 90,
							show: false
						}
					]
				: []),
			...(isAuthorized('IMSModule') && formMode !== 'add' && !isImportOrder
				? [
						{
							header: 'productType',
							accessorKey: 'itemType',
							size: 100,
							maxSize: 100,
							renderCell: ({ row }) => <StringInput viewMode value={t(row.original.itemType)} />
						}
					]
				: []),
			{
				header: 'quantity',
				accessorKey: 'quantity',
				size: 90,
				maxSize: 90,
				renderCell: ({ row }) => (
					<IntegerInput
						errorMessage={errors?.products?.[row.index]?.quantity?.message}
						value={watch(`products[${row.index}].quantity`)}
						setValue={(value) => {
							setValue(`products[${row.index}].quantity`, value, {
								shouldValidate: true,
								shouldDirty: true
							});
							const val =
								value *
								((getValues(`products[${row.index}].price`) ?? 0) +
									(getValues(`products[${row.index}].tax`) ?? 0));
							setValue(`products[${row.index}].total`, val ? Number(val.toFixed(2)) : val, {
								shouldValidate: true,
								shouldDirty: true
							});
						}}
						viewMode={viewMode || manuallyAdded === false || !editable || hasWave}
					/>
				)
			},
			{
				header: 'price',
				accessorKey: 'price',
				size: 100,
				maxSize: 150,
				renderCell: ({ row }) => (
					<IntegerInput
						value={watch(`products[${row.index}].price`)}
						setValue={(value) => {
							setValue(`products[${row.index}].price`, value, {
								shouldValidate: true,
								shouldDirty: true
							});
							setValue(
								`products[${row.index}].total`,
								getValues(`products[${row.index}].quantity`) *
									((value ?? 0) + (getValues(`products[${row.index}].tax`) ?? 0)),
								{
									shouldValidate: true,
									shouldDirty: true
								}
							);
						}}
						unit={watch(`products[${row.index}].currency`) ?? Currency}
						viewMode={viewMode || manuallyAdded === false || !editable || hasWave}
						showArrows={false}
						allowFloat
					/>
				)
			},
			...(formMode !== 'add' && !isImportOrder
				? [
						{
							header: 'taxPercent',
							accessorKey: 'taxPercent',
							size: 80,
							show: false,
							renderCell: ({ row }) => {
								const taxValue = watch(`products[${row.index}].taxPercent`);
								const textValue = taxValue ? `${taxValue} %` : undefined;

								return <Typography>{textValue}</Typography>;
							}
						},
						{
							header: 'discountAmount',
							accessorKey: 'discountAmount',
							size: 80,
							show: false,
							renderCell: ({ row }) => (
								<IntegerInput
									name={`products[${row.index}].discountAmount`}
									unit={watch(`products[${row.index}].currency`) ?? Currency}
									viewMode
								/>
							)
						},
						{
							header: 'subtotal',
							accessorKey: 'rowTotal',
							size: 80,
							show: false,
							renderCell: ({ row }) => (
								<IntegerInput
									name={`products[${row.index}].rowTotal`}
									unit={watch(`products[${row.index}].currency`) ?? Currency}
									viewMode
								/>
							)
						},
						{
							header: 'rowTotalInclTax',
							accessorKey: 'rowTotalInclTax',
							size: 120,
							show: false,
							renderCell: ({ row }) => (
								<IntegerInput
									name={`products[${row.index}].rowTotalInclTax`}
									unit={watch(`products[${row.index}].currency`) ?? Currency}
									viewMode
								/>
							)
						}
					]
				: []),
			{
				header: 'tax',
				accessorKey: 'tax',
				size: 100,
				maxSize: 150,
				renderCell: ({ row }) => (
					<IntegerInput
						value={watch(`products[${row.index}].tax`)}
						setValue={(value) => {
							setValue(`products[${row.index}].tax`, value, {
								shouldValidate: true,
								shouldDirty: true
							});
							setValue(
								`products[${row.index}].total`,
								getValues(`products[${row.index}].quantity`) *
									((getValues(`products[${row.index}].price`) ?? 0) + (value ?? 0)),
								{
									shouldValidate: true,
									shouldDirty: true
								}
							);
						}}
						unit={watch(`products[${row.index}].currency`) ?? Currency}
						viewMode={viewMode || manuallyAdded === false || !editable || hasWave}
						showArrows={false}
						allowFloat
					/>
				)
			},
			{
				header: 'total',
				accessorKey: 'total',
				size: 100,
				maxSize: 150,
				renderCell: ({ row }) => (
					<IntegerInput
						name={`products[${row.index}].total`}
						unit={watch(`products[${row.index}].currency`) ?? Currency}
						viewMode={viewMode || manuallyAdded === false || !editable || hasWave}
						showArrows={false}
						allowFloat
					/>
				)
			},
			...((editMode || addMode) && manuallyAdded && editable
				? [
						{
							id: 'deleteRow',
							size: isMobile ? 10 : 20,
							maxSize: isMobile ? 10 : 20,
							showOnMobile: true,
							enableHiding: false
						}
					]
				: [])
		],
		[isMobile, errors, isInternationalOrder]
	);

	const handleAddBox = useCallback(() => {
		appendBox(defaultBox);
	}, [defaultBox]);

	const handleAddProduct = useCallback(() => {
		append({
			productID: null,
			name: null,
			hsCode: null,
			SKU: null,
			quantity: 1,
			price: null,
			tax: null,
			total: null,
			currency
		});
	}, [currency]);

	useUpdateEffect(() => {
		if (addMode || editMode) {
			const currentWeight = getValues('weight');
			const currentPackageCount = getValues('packageCount');

			if (boxWatcher?.length > 0) {
				const newWeight = roundNumber(boxWatcher.reduce((acc, curr) => acc + curr.weight, 0));
				const newPackageCount = boxWatcher.length;
				if (currentWeight !== newWeight) {
					setValue('weight', newWeight, {
						shouldValidate: true,
						shouldDirty: true
					});
				}
				if (currentPackageCount !== newPackageCount) {
					setValue('packageCount', newPackageCount, {
						shouldValidate: true,
						shouldDirty: true
					});
				}
			}
		}
	}, [boxWatcher]);

	const onRemove = useCallback(async (rowID) => {
		try {
			const response = await axios.post(ORDER_ACTIONS.DELETE_ORDER_BOX, {
				otoOrderID: Number(otoOrderID),
				ID: rowID
			});
			if (response.status === 200) {
				showSuccess();
			} else {
				showError();
				AppLogger.error(response);
			}
		} catch (error) {
			AppLogger.error(error);
			showError();
		}
	}, []);

	const handleAdjustOrderValue = useCallback(() => {
		setValue('grandTotal', grandTotalOfAllProducts, { shouldValidate: true, shouldDirty: true });
		// when adjusting the order value, if the payment method is COD, set the total due to the grand total
		// otherwise, set the total due to null
		if (paymentMethod?.toLowerCase() === 'cod') {
			setValue('totalDue', grandTotalOfAllProducts);
		} else {
			setValue('totalDue', null);
		}
		showSuccess({
			header: 'orderValueAdjusted',
			message: 'orderValueAdjustedMessage'
		});
		orderDetailsAccordionRef.current.open();
		setTimeout(() => {
			setFocus('grandTotal');
		}, 300);
	}, [grandTotalOfAllProducts]);

	useEffect(() => {
		orderDetailsErrorFields.some((field) => {
			if (errors[field]) {
				orderDetailsAccordionRef.current.open();
				return true;
			}
		});
		productDetailsErrorFields.some((field) => {
			if (errors[field]) {
				productDetailsAccordionRef.current.open();
				return true;
			}
		});
		boxDetailsErrorFields.some((field) => {
			if (errors[field]) {
				boxDetailsAccordionRef.current.open();
				return true;
			}
		});

		if (addMode && errors?.products?.type === 'internationalOrderProductRequired') {
			showError({
				header: errors?.products?.type,
				message: errors?.products?.message
			});

			append({
				productID: null,
				name: null,
				hsCode: null,
				SKU: null,
				quantity: 1,
				price: null,
				tax: null,
				total: null,
				currency
			});
		}
	}, [errors]);

	return (
		<>
			<S.AccordionContainer>
				<Accordion ref={orderDetailsAccordionRef} header="orderDetails" defaultVisible>
					<FormItem
						colSpan={defaultColSpan}
						label="orderNumber"
						isLoading={isLoading}
						required
						childStyle={{
							display: 'flex',
							flexDirection: 'row',
							gap: '1rem'
						}}>
						<div
							style={{
								display: 'flex',
								flexDirection: 'column',
								width: '100%'
							}}>
							<StringInput disabled={formMode !== 'add'} name="orderID" />
						</div>
						{addMode && (
							<S.OrderNumberGeneratorButton
								variant="outline"
								label="generate"
								loading={generateButtonLoading}
								onClick={getAutoFillOrderID}
							/>
						)}
					</FormItem>

					<FormItem colSpan={defaultColSpan} label="orderDate" isLoading={isLoading} required>
						<DateInput
							name="orderDate"
							value={watch('orderDate')}
							disabled={
								viewMode || manuallyAdded === false || (!isAdmin && isCustomerServiceUser && editMode)
							}
							setValue={(value) => {
								setValue('orderDate', value, { shouldValidate: true, shouldDirty: true });
							}}
							enableTime
						/>
					</FormItem>

					<FormItem colSpan={defaultColSpan} label="paymentMethod" isLoading={isLoading}>
						<EnumSelect
							name="paymentMethod"
							data={paymentMethods.split(',')}
							searchable={false}
							valueRemovable={false}
							disabled={
								viewMode ||
								manuallyAdded === false ||
								(!isAdmin && isCustomerServiceUser && editMode) ||
								isImportOrder
							}
							placeholder="selectOne"
							value={watch('paymentMethod')}
							setValue={(value) => {
								setValue('paymentMethod', value, { shouldValidate: true, shouldDirty: true });
							}}
						/>
					</FormItem>

					<FormItem
						colSpan={paymentMethod?.toLowerCase() !== 'cod' ? defaultColSpan : smallDefaultColSpan}
						label="grandTotal"
						isLoading={isLoading}
						required>
						<CurrencyInput
							name="grandTotal"
							disabled={
								viewMode || manuallyAdded === false || (!isAdmin && isCustomerServiceUser && editMode)
							}
							value={currency}
							setValue={(value) => {
								setValue(`currency`, value, { shouldValidate: true, shouldDirty: true });
							}}
							currencyErrorMessage={errors?.currency?.message}
						/>
					</FormItem>

					{paymentMethod?.toLowerCase() === 'cod' && (
						<FormItem colSpan={smallDefaultColSpan} label="totalDue" isLoading={isLoading} required>
							<CurrencyInput
								name="totalDue"
								disabled={
									viewMode ||
									manuallyAdded === false ||
									(!isAdmin && isCustomerServiceUser && editMode)
								}
								value={currency}
								setValue={(value) => {
									setValue(`currency`, value, { shouldValidate: true, shouldDirty: true });
								}}
								currencyErrorMessage={errors?.currency?.message}
							/>
						</FormItem>
					)}

					{!isImportOrder && (
						<FormItem
							colSpan={defaultColSpan}
							label="warehouse"
							roleName="/autocomplete/warehouse"
							isLoading={isLoading}>
							<AutoComplete
								disabled={formMode !== 'add'}
								autoCompleteName={AUTOCOMPLETE.WAREHOUSES}
								setValue={(value) => {
									const warehouseValue = value?.ID ?? null;
									const warehouseCountry = value?.Country ?? null;
									if (addMode) {
										setValue('warehouseID', warehouseValue, {
											shouldValidate: true,
											shouldDirty: true
										});
										setValue('originCountry', warehouseCountry, {
											shouldValidate: true,
											shouldDirty: true
										});
									}
									if (warehouseValue !== null && addMode) {
										setValue('branchID', null, {
											shouldValidate: true,
											shouldDirty: true
										});
									}
								}}
								value={warehouseID}
								columns={warehouseAutocompleteColumns}
								selectionLabelAccessor={warehouseAutocompleteLabels}
								firstItem={
									isAuthorized('/autocomplete/warehouse/addANewWarehouse')
										? {
												action: () =>
													NiceModal.show(WarehouseFormShort, {
														setWarehouse: (value) => {
															setValue('warehouseID', value, {
																shouldValidate: true,
																shouldDirty: true
															});
														}
													}),
												label: <Typography color="var(--blue)">addANewWarehouse</Typography>
											}
										: undefined
								}
							/>
						</FormItem>
					)}

					{!isImportOrder && (
						<FormItem
							colSpan={defaultColSpan}
							label="branch"
							isLoading={isLoading}
							roleName="/autocomplete/branch">
							<AutoComplete
								disabled={formMode !== 'add'}
								autoCompleteName={AUTOCOMPLETE.BRANCHES}
								setValue={(value) => {
									const branchValue = value?.ID ?? null;
									const branchCountry = value?.Country ?? null;
									if (addMode) {
										setValue(`branchID`, branchValue, {
											shouldValidate: true,
											shouldDirty: true
										});
										setValue('originCountry', branchCountry, {
											shouldValidate: true,
											shouldDirty: true
										});
									}
									if (branchValue !== null && addMode) {
										setValue('warehouseID', null, {
											shouldValidate: true,
											shouldDirty: true
										});
									}
								}}
								value={branchID}
								columns={branchAutocompleteColumns}
								selectionLabelAccessor={branchAutocompleteLabels}
								firstItem={
									isAuthorized('/autocomplete/branch/addANewBranch')
										? {
												action: () =>
													NiceModal.show(BranchFormshort, {
														setBranch: (value) => {
															setValue(`branchID`, value, {
																shouldValidate: true,
																shouldDirty: true
															});
														}
													}),
												label: <Typography color="var(--blue)">addANewBranch</Typography>
											}
										: undefined
								}
							/>
						</FormItem>
					)}

					<FormItem
						colSpan={defaultColSpan}
						label="brand"
						isLoading={isLoading}
						roleName="/autocomplete/brand"
						required={userType === 'BrandManager'}>
						<AutoComplete
							name="brandID"
							autoCompleteName={AUTOCOMPLETE.BRANDS}
							columns={brandAutocompleteColumns}
							selectionLabelAccessor={['Name']}
							disabled={viewMode || manuallyAdded === false}
							setValue={(value) => {
								setValue(`brandID`, value?.ID ?? null, {
									shouldValidate: true,
									shouldDirty: true
								});
							}}
							value={brandID}
							firstItem={
								isAuthorized('/autocomplete/brand/addANewBrand')
									? {
											action: () =>
												NiceModal.show(BrandForm, {
													setBrand: (value) => {
														setValue(`brandID`, value, {
															shouldValidate: true,
															shouldDirty: true
														});
													}
												}),
											label: <Typography color="var(--blue)">addANewBrand</Typography>,
											disabled: (dataSize) => isRestricted(SYSTEM_LIMITS.BRAND, dataSize),
											disabledTooltip: (
												<div style={{ display: 'block' }}>
													<ModuleToolTipContainer
														infoImage="lottie_multiBrand"
														title="multiBrandModule"
														description="multiBrandModuleSubtitle"
													/>

													<Button
														style={{ marginLeft: 'Auto', marginRight: 'Auto' }}
														className="mt-3"
														label="showPlans"
														icon={faEye}
														variant="blue"
														onMouseDown={() => {
															track('moreThan3Brands');
															navigate(PATHS.OTO_MODULES);
														}}
													/>
												</div>
											)
										}
									: undefined
							}
						/>
					</FormItem>

					{!isImportOrder && (
						<FormItem
							colSpan={defaultColSpan}
							label="refNo"
							isLoading={isLoading}
							toolTip="refNo.tooltip"
							roleName="EnterprisePackage">
							<StringInput
								disabled={viewMode || (!isAdmin && isCustomerServiceUser && editMode)}
								name="refNo"
							/>
						</FormItem>
					)}
				</Accordion>
				<Accordion
					ref={productDetailsAccordionRef}
					header="productDetails"
					headerTooltip="productDetailsAccordionTooltip"
					defaultVisible>
					<FormItem
						colSpan={largeDefaultColSpan}
						label="itemDescription"
						isLoading={isLoading}
						required={!productsWatcher?.length}>
						<TextArea
							disabled={viewMode || (!isAdmin && isCustomerServiceUser && editMode)}
							name="orderComments"
							rows="2"
							placeholder="orderCommentsPlaceholder"
						/>
					</FormItem>

					<FormItem>
						<Table
							columns={columns}
							data={controlledFields}
							loading={isLoading}
							noRecordsFound={
								viewMode ? (
									<NoRecordsFound
										lottieName="no-items"
										height="10rem"
										title="noItems"
										subTitle="noItemsSubtitle"
									/>
								) : (
									<></>
								)
							}
							onRowDelete={removeItem}
							deletableLastRow={!isImportOrder}
							storageKey={
								formMode !== 'add'
									? isImportOrder
										? 'importOrderDetailsTable'
										: 'orderDetailsTable'
									: undefined
							}
							showColumnsMenu={formMode !== 'add'}
						/>
					</FormItem>
					{formMode !== 'view' && manuallyAdded && editable && !hasWave && (
						<FormItem classNameChild="products-buttons-form-item">
							<Button label="addNewRow" icon={faPlus} variant="outline" onClick={handleAddProduct} />
							<Button
								label="adjustOrderValue"
								variant="transparent"
								onClick={handleAdjustOrderValue}
								style={{
									textDecoration: 'underline'
								}}
								disabled={
									controlledFields?.length < 1 ||
									grandTotalOfAllProducts < 1 ||
									grandTotalOfAllProducts === grandTotal
								}
							/>
						</FormItem>
					)}
				</Accordion>
				<Accordion
					ref={boxDetailsAccordionRef}
					header="boxDetails"
					headerTooltip="boxDetailsAccordionTooltip"
					defaultVisible>
					{boxFields?.map((field, index) => (
						<BoxRow
							key={`${field.id}${index}`}
							index={index}
							remove={removeBox}
							fieldName="boxes"
							disabled={viewMode || (!isAdmin && isCustomerServiceUser && editMode)}
							disabledDeleteButton={isImportOrder && boxFields.length <= 1}
							onRemove={onRemove}
						/>
					))}

					<FormItem colSpan={defaultColSpan} label="packageCount" isLoading={isLoading}>
						<IntegerInput
							disabled={
								viewMode || (!isAdmin && isCustomerServiceUser && editMode) || boxFields?.length > 0
							}
							name="packageCount"
							unit="pcs"
						/>
					</FormItem>

					<FormItem colSpan={defaultColSpan} label="weight" required isLoading={isLoading}>
						<IntegerInput
							disabled={
								viewMode || (!isAdmin && isCustomerServiceUser && editMode) || boxFields?.length > 0
							}
							name="weight"
							unit="kg"
							decimalLimit={2}
							allowFloat
						/>
					</FormItem>

					{!(viewMode || (!isAdmin && isCustomerServiceUser && editMode)) && (
						<FormItem>
							<Button
								label="addBox"
								icon={faPlus}
								variant="outline"
								onClick={handleAddBox}
								css={css`
									@media ${Breakpoints.mobile} {
										width: 100%;
									}
								`}
							/>
						</FormItem>
					)}
				</Accordion>
				<Alert showIcon variant="info" content={'boxDetail.alert.message'} />
			</S.AccordionContainer>
		</>
	);
};

const AutoStringCell = ({ hsCode, setHsCode, formMode, errorMessage }) => {
	const axios = useAxiosPrivate();

	const debouncedText = useDebounce(hsCode, 1000);

	const [hsCodes, setHsCodes] = useState([]);

	useEffect(() => {
		if (!debouncedText || debouncedText.length < 3) {
			setHsCodes([]);
			return;
		}
		axios
			.post(PRODUCTS.GET_HS_CODES, {
				keyword: debouncedText || ''
			})
			.then((response) => {
				if (response.status === 200) {
					setHsCodes(response.data);
				}
			})
			.catch((error) => {
				AppLogger.error(error);
			});
	}, [debouncedText]);

	return (
		<AutoString
			name="ID"
			columns={[
				{ accessor: 'ID', width: 100 },
				{ accessor: 'Name', width: 400 }
			]}
			autoFilter={false}
			data={hsCodes}
			value={hsCode}
			setValue={setHsCode}
			viewMode={formMode === 'view'}
			errorMessage={errorMessage}
			placeholder={'hsCodePlaceholder'}
			fixedContainerWidth={false}
		/>
	);
};

export default OrderDetails;
